import type { PossibleProvider } from '@/types/dashboard'
import type { OrganizationProviderRow } from '@/hooks/useOrganizationProviders'
import { decodeRelayId } from '@/utils/id'

export interface EligibleProvider {
  profileId: string
  fullName: string
  phoneNumber?: string
}

function providerUuid(id: string | null | undefined): string | null {
  if (!id) return null
  return decodeRelayId(id) ?? id
}

/**
 * Returns providers that exist in both possibleProviders (service) and organizationProviders (org roster).
 */
export function getEligibleProviders(
  possibleProviders: PossibleProvider[],
  organizationProviders: OrganizationProviderRow[],
): EligibleProvider[] {
  const orgIds = new Set<string>()
  for (const org of organizationProviders) {
    const uuid = providerUuid(org.id)
    if (uuid) orgIds.add(uuid)
  }

  const seen = new Set<string>()
  const eligible: EligibleProvider[] = []

  for (const pp of possibleProviders) {
    const profileId = providerUuid(pp.provider?.id)
    if (!profileId || !orgIds.has(profileId) || seen.has(profileId)) continue
    seen.add(profileId)
    eligible.push({
      profileId,
      fullName: pp.provider?.fullName?.trim() || '—',
      phoneNumber: pp.provider?.phoneNumber ?? undefined,
    })
  }

  return eligible.sort((a, b) => a.fullName.localeCompare(b.fullName))
}

export function filterEligibleProvidersBySearch(
  providers: EligibleProvider[],
  query: string,
): EligibleProvider[] {
  const q = query.trim().toLowerCase()
  if (!q) return providers
  return providers.filter(
    (p) =>
      p.fullName.toLowerCase().includes(q) ||
      (p.phoneNumber?.toLowerCase().includes(q) ?? false),
  )
}
